import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { authedFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { LaunchStatusBanner } from "../components/LaunchStatusBanner";
import { Input } from "../components/ui/input";
import { SuburbAutocomplete } from "../components/SuburbAutocomplete";
import { DirectoryMultiSelect } from "../components/DirectoryMultiSelect";
import { DemoBanner, DemoCoachmark, DemoFooter } from "../components/DemoBanner";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Search, MapPin, Stethoscope, BadgeCheck, Mail, Phone, CalendarClock, X, SlidersHorizontal,
  KeyRound, CheckCircle2, Loader2, Copy, ShieldCheck, Languages, FlaskConical,
  MousePointerClick, Users, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { genToken, bookingSnippet } from "../lib/bookingTokens";
import { SAMPLE_CLINICIANS } from "../lib/sampleClinicians";
import { getDemoTourStep, saveDemoToken, setDemoTourStep, skipDemoTour } from "../lib/demoSession";
import { APPOINTMENT_TYPE_OPTIONS, normaliseExpertiseList } from "../lib/clinicalOptions";
import { capacityCardText, capacityDetail, psychReferralLabel } from "../lib/capacity";

// Map enum values -> human label + visual tone. Inline hex `style` bypasses the
// global tailwind palette remap so the statuses stay visually distinct.
import { AVAIL_OPTIONS } from "../lib/capacity";
const AVAILABILITY = Object.fromEntries(AVAIL_OPTIONS.map((o) => [o.value, { label: o.label, tone: "border", style: o.style }]));

const availabilityMeta = (v) =>
  AVAILABILITY[v] || { label: v || "—", tone: "border", style: { backgroundColor: "#F1F5F9", color: "#475569", borderColor: "#E2E8F0" } };

const initials = (c) => `${(c.first_name || "")[0] || ""}${(c.last_name || "")[0] || ""}`.toUpperCase();
const expertiseFor = (c) => normaliseExpertiseList(
  Array.isArray(c?.clinical_expertise) && c.clinical_expertise.length ? c.clinical_expertise : (c?.specialty || "").split(",")
);

// Nationwide AU suburb/postcode -> {lat,lng} lookup, lazily loaded once. No hardcoded region-specific relationships.
let auSuburbIndexPromise = null;
const loadAuSuburbIndex = () => {
  if (!auSuburbIndexPromise) {
    auSuburbIndexPromise = fetch("/data/au-suburbs.json")
      .then((response) => (response.ok ? response.json() : []))
      .then((rows) => {
        const byKey = new Map();
        const byPostcode = new Map();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          if (typeof row.lat !== "number" || typeof row.lng !== "number") return;
          const suburbKey = String(row.s || "").trim().toLowerCase();
          const postcode = String(row.p || "").trim();
          const coords = { latitude: row.lat, longitude: row.lng };
          if (suburbKey && postcode) byKey.set(`${postcode}|${suburbKey}`, coords);
          if (postcode && !byPostcode.has(postcode)) byPostcode.set(postcode, coords);
        });
        return { byKey, byPostcode };
      })
      .catch(() => ({ byKey: new Map(), byPostcode: new Map() }));
  }
  return auSuburbIndexPromise;
};
const coordinatesFromIndex = (index, suburb, postcode) => {
  if (!index) return null;
  const suburbKey = String(suburb || "").trim().toLowerCase();
  const pc = String(postcode || "").trim();
  if (suburbKey && pc && index.byKey.has(`${pc}|${suburbKey}`)) return index.byKey.get(`${pc}|${suburbKey}`);
  if (pc && index.byPostcode.has(pc)) return index.byPostcode.get(pc);
  return null;
};
const distanceKm = (from, to) => {
  if (!from || !to) return null;
  const radians = (degrees) => degrees * Math.PI / 180;
  const dLat = radians(to.latitude - from.latitude);
  const dLng = radians(to.longitude - from.longitude);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(from.latitude)) * Math.cos(radians(to.latitude)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Headshot if uploaded, otherwise initials fallback.
const Avatar = ({ c, size = "md" }) => {
  const dim = size === "lg" ? "h-14 w-14" : "h-12 w-12";
  const txt = size === "lg" ? "text-lg" : "text-base";
  if (c.photo_url) {
    return (
      <img
        src={c.photo_url}
        alt={`${c.first_name} ${c.last_name}`}
        className={`${dim} shrink-0 rounded-md border border-slate-200 object-cover`}
        data-testid={`clinician-photo-${c.id}`}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  return (
    <div className={`flex ${dim} shrink-0 items-center justify-center rounded-md bg-secondary font-heading ${txt} font-semibold text-secondary-foreground`}>
      {initials(c)}
    </div>
  );
};

export default function Clinicians({ sampleMode = false, adminRolloutMode = false, readOnlyMode = false, embeddedMode = false, viewAsMode = false, demoLinkMode = false, demoFirstName, demoNav, demoExitPath = "/clinicians", demoOnExit, demoScope = "sample-demo", demoTrackEvent }) {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gpUserId, setGpUserId] = useState(null);
  const [gpClinicName, setGpClinicName] = useState("");
  const [meClinician, setMeClinician] = useState(null);
  const [clinicTeam, setClinicTeam] = useState([]);
  const [clinicReferrerId, setClinicReferrerId] = useState("");
  const [profession, setProfession] = useState(sampleMode || adminRolloutMode ? "all" : "");
  const [specialty, setSpecialty] = useState("");
  const [suburb, setSuburb] = useState("");
  const [radiusKm, setRadiusKm] = useState("10");
  const [suburbLocation, setSuburbLocation] = useState(null); // locked-in {suburb, postcode, state, lat, lng} selection
  const [auSuburbIndex, setAuSuburbIndex] = useState(null);
  useEffect(() => {
    let alive = true;
    loadAuSuburbIndex().then((index) => { if (alive) setAuSuburbIndex(index); });
    return () => { alive = false; };
  }, []);
  const handleSuburbChange = (value) => { setSuburb(value); setSuburbLocation(null); };
  const handleSuburbSelect = (location) => {
    if (location && Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
      setSuburbLocation(location);
    } else {
      setSuburbLocation(null);
    }
  };
  const [availability, setAvailability] = useState("all");
  const [language, setLanguage] = useState("English");
  const [gender, setGender] = useState("all");
  const [appointmentType, setAppointmentType] = useState("all");
  const [selected, setSelected] = useState(null);
  const [tourStep, setTourStep] = useState(() => getDemoTourStep(demoScope));
  const [launchLive, setLaunchLive] = useState(false);
  const [demoCompletionOpen, setDemoCompletionOpen] = useState(false);
  const [introPromptOpen, setIntroPromptOpen] = useState(false);
  const appearanceKeyRef = useRef("");

  useEffect(() => {
    if (sampleMode || !user) return;
    authedFetch("/site/launch-status").then(async (response) => {
      if (!response.ok) return;
      const status = await response.json();
      const live = Boolean(status.is_live);
      setLaunchLive(live);
      const completed = Boolean(user.user_metadata?.sample_directory_completed);
      const dismissed = sessionStorage.getItem("sample-directory-completion-dismissed") === "1";
      const joinedAfterLaunch = Boolean(status.launched_at && user.created_at && new Date(user.created_at) >= new Date(status.launched_at));
      if (live && joinedAfterLaunch && !completed && !dismissed) setDemoCompletionOpen(true);
    }).catch(() => {});
  }, [sampleMode, user]);

  useEffect(() => {
    if (sampleMode || role !== "gp" || !user) return;
    const loginCount = Number(user.user_metadata?.login_count || 0);
    if (loginCount >= 2 && !user.user_metadata?.introduction_prompt_answered_at) setIntroPromptOpen(true);
  }, [role, sampleMode, user]);

  const saveIntroductionPreference = async (accepted) => {
    const { error } = await supabase.auth.updateUser({ data: {
      ...user.user_metadata,
      accept_clinician_introductions: accepted,
      introduction_prompt_answered_at: new Date().toISOString(),
    }});
    if (error) return toast.error("Could not save your preference.");
    setIntroPromptOpen(false);
    toast.success(accepted ? "You can now receive selected clinician introductions through SimplyRefer." : "Preference saved. You can change this later in Settings.");
  };

  const completeSampleDirectory = async () => {
    const { error } = await supabase.auth.updateUser({ data: { sample_directory_completed: true } });
    if (error) return toast.error("Could not save this preference.");
    setDemoCompletionOpen(false);
    toast.success("Got it. The Sample Directory will always remain available at the top-right of the live directory, above the filters.");
  };

  const advanceTour = (nextStep) => {
    if (!sampleMode || tourStep >= 99) return;
    setDemoTourStep(demoScope, nextStep);
    setTourStep(nextStep);
  };

  const skipTourHere = () => {
    skipDemoTour(demoScope);
    setTourStep(99);
  };

  useEffect(() => {
    const refreshTour = (event) => {
      if (!event.detail || event.detail.scope === demoScope) setTourStep(getDemoTourStep(demoScope));
    };
    window.addEventListener("simplyrefer-demo-tour", refreshTour);
    return () => window.removeEventListener("simplyrefer-demo-tour", refreshTour);
  }, [demoScope]);

  useEffect(() => {
    // Sample mode: fully isolated, browser-only fictional data. No Supabase.
    if (sampleMode) {
      setClinicians(SAMPLE_CLINICIANS);
      setGpUserId(null);
      setGpClinicName("");
      setMeClinician(null);
      setLoading(false);
      return;
    }
    if (!user) { setLoading(false); return; }
    if (!launchLive && !adminRolloutMode) {
      setClinicians([]);
      setGpUserId(null);
      setGpClinicName("");
      setMeClinician(null);
      setLoading(false);
      return;
    }
    let active = true;
    const load = async () => {
      const [clinRes, gpRes, meRes] = await Promise.all([
        supabase.from("clinicians").select("*")
          .eq("is_verified", true)
          .eq("is_active", true)
          .eq("is_test", false)
          .order("last_name", { ascending: true }),
        supabase.from("gp_users").select("id,clinic_name").eq("auth_user_id", user.id).maybeSingle(),
        supabase.from("clinicians").select("id,first_name,last_name,profession").eq("email", user.email).maybeSingle(),
      ]);
      if (!active) return;
      if (clinRes.error) toast.error("Could not load clinicians: " + clinRes.error.message);
      // Live directory shows genuine clinicians only — fictional samples (is_sample) are excluded.
      setClinicians((clinRes.data || []).filter((c) => c.is_sample !== true));
      setGpUserId(gpRes.data?.id || null);
      setGpClinicName(gpRes.data?.clinic_name || "");
      setMeClinician(meRes.data || null);
      if (role === "clinic") {
        const { data: account } = await supabase.from("accounts").select("id").eq("billing_email", user.email).maybeSingle();
        const { data: team } = account ? await supabase.from("clinicians").select("id,first_name,last_name,profession,is_verified,is_active").eq("account_id", account.id).eq("is_verified", true).eq("is_active", true).order("last_name") : { data: [] };
        if (!active) return;
        setClinicTeam(team || []);
        setClinicReferrerId((team || [])[0]?.id || "");
      }
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [user, sampleMode, role, launchLive, adminRolloutMode]);

  const availabilityOptions = useMemo(() => Object.keys(AVAILABILITY), []);

  const specialtyOptions = useMemo(() => {
    if (!profession) return [];
    const set = new Set();
    clinicians.forEach((c) => {
      if (profession !== "all" && (c.profession || "").toLowerCase() !== profession.toLowerCase()) return;
      const expertise = Array.isArray(c.clinical_expertise) && c.clinical_expertise.length
        ? c.clinical_expertise
        : (c.specialty || "").split(",");
      normaliseExpertiseList(expertise).forEach((item) => set.add(item));
    });
    return [...set].filter((item) => item !== "Adult").sort((a, b) => a.localeCompare(b));
  }, [clinicians, profession]);

  const languageOptions = useMemo(() => {
    const set = new Set(["English"]);
    clinicians.forEach((c) => {
      (c.languages || "").split(",").forEach((s) => { const t = s.trim(); if (t) set.add(t); });
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clinicians]);

  const genderOptions = useMemo(() => {
    const set = new Set(["Female", "Male", "Non-binary", "Prefer not to say"]);
    clinicians.forEach((c) => { if (c.gender) set.add(c.gender.trim()); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clinicians]);

  const filtered = useMemo(() => {
    if (!profession) return [];
    return clinicians.filter((c) => {
      if (meClinician && c.id === meClinician.id) return false; // don't refer to yourself
      if (clinicTeam.some((member) => member.id === c.id)) return false; // don't refer within your own clinic team
      if (profession !== "all" && (c.profession || "").toLowerCase() !== profession.toLowerCase()) return false;
      const selectedSpecialties = specialty ? specialty.split(",").map((item) => item.trim().toLowerCase()).filter(Boolean) : [];
      if (selectedSpecialties.length) {
        const source = Array.isArray(c.clinical_expertise) && c.clinical_expertise.length ? c.clinical_expertise : (c.specialty || "").split(",");
        const specs = normaliseExpertiseList(source).map((item) => item.toLowerCase());
        if (!selectedSpecialties.some((selectedSpecialty) => specs.includes(selectedSpecialty))) return false;
      }
      if (appointmentType !== "all") {
        const appointments = (c.appointment_types || []).map((item) => String(item).trim().toLowerCase());
        if (!appointments.includes(appointmentType.toLowerCase())) return false;
      }
      if (suburbLocation) {
        const origin = { latitude: suburbLocation.lat, longitude: suburbLocation.lng };
        const destination = coordinatesFromIndex(auSuburbIndex, c.suburb, c.postcode);
        const distance = distanceKm(origin, destination);
        if (distance === null || distance > Number(radiusKm)) return false;
      }
      if (availability !== "all" && c.availability_status !== availability) return false;
      if (language !== "all") {
        const langs = (c.languages || "").split(",").map((s) => s.trim().toLowerCase());
        if (!langs.includes(language.toLowerCase())) return false;
      }
      if (gender !== "all" && (c.gender || "") !== gender) return false;
      return true;
    });
  }, [clinicians, profession, specialty, appointmentType, suburbLocation, radiusKm, availability, language, gender, meClinician, clinicTeam, auSuburbIndex]);

  useEffect(() => {
    if (sampleMode || role !== "gp" || !gpUserId || loading || !filtered.length) return;
    const key = [profession, specialty, appointmentType, suburb, availability, language, gender, filtered.map((item) => item.id).join(",")].join("|");
    if (appearanceKeyRef.current === key) return;
    const timer = window.setTimeout(() => {
      appearanceKeyRef.current = key;
      authedFetch("/analytics/clinician-events", {
        method: "POST",
        body: JSON.stringify({ event_type: "SEARCH_APPEARANCE", clinician_ids: filtered.map((item) => item.id) }),
      }).catch(() => {});
    }, 600);
    return () => window.clearTimeout(timer);
  }, [sampleMode, role, gpUserId, loading, filtered, profession, specialty, appointmentType, suburb, availability, language, gender]);

  const openClinician = (clinician) => {
    setSelected(clinician);
    if (tourStep === 4) advanceTour(5);
    if (sampleMode) {
      demoTrackEvent?.("PROFILE_VIEWED", { clinician_id: clinician.id, profession: clinician.profession });
    } else if (!readOnlyMode && role === "gp" && gpUserId) {
      authedFetch("/analytics/clinician-events", {
        method: "POST",
        body: JSON.stringify({ event_type: "PROFILE_VIEW", clinician_ids: [clinician.id] }),
      }).catch(() => {});
    }
  };

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveAlerts, setSaveAlerts] = useState(true);
  const [saving, setSaving] = useState(false);

  const saveSearch = async () => {
    if (!gpUserId || !saveName.trim()) return;
    setSaving(true);
    const criteria = {
      profession, specialty, suburb, radiusKm: Number(radiusKm) || 10,
      suburbLocation, availability, language, gender, appointmentType,
    };
    const { error } = await supabase.from("gp_saved_searches").insert({
      gp_user_id: gpUserId,
      name: saveName.trim(),
      criteria,
      alerts_enabled: saveAlerts,
    });
    setSaving(false);
    if (error) {
      toast.error("Could not save this search");
    } else {
      toast.success("Search saved" + (saveAlerts ? " \u2014 you'll be alerted about new matches" : ""));
      setSaveOpen(false);
      setSaveName("");
    }
  };

  const clearAll = () => {
    setProfession(sampleMode || adminRolloutMode ? "all" : ""); setSpecialty(""); setAppointmentType("all"); setSuburb(""); setSuburbLocation(null); setRadiusKm("10"); setAvailability("all"); setLanguage("all"); setGender("all");
  };

  const hasFilters = Boolean((profession && profession !== "all") || specialty || appointmentType !== "all" || suburb || availability !== "all" || language !== "all" || gender !== "all");

  useEffect(() => {
    if (!sampleMode || !hasFilters || !demoTrackEvent) return;
    const timer = window.setTimeout(() => {
      demoTrackEvent("FILTER_USED", { profession, expertise: specialty, appointment_type: appointmentType, suburb, availability, language, gender });
    }, 500);
    return () => window.clearTimeout(timer);
    // Tracking callback is intentionally excluded so filter changes, not parent renders, create events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleMode, hasFilters, profession, specialty, appointmentType, suburb, availability, language, gender]);

  // Who is referring? GPs use their gp_users record; clinicians refer against their own profile.
  const referrer = sampleMode
    ? { mode: "sample", defaultName: demoLinkMode ? "Harbour Family Practice" : "Sample Family Practice" }
    : gpUserId
    ? { mode: "gp", gpUserId, defaultName: gpClinicName }
    : meClinician
    ? {
        mode: "clinician",
        clinicianId: meClinician.id,
        defaultName: `${meClinician.first_name || ""} ${meClinician.last_name || ""}`.trim() +
          (meClinician.profession ? ` — ${meClinician.profession}` : ""),
      }
    : role === "clinic" && clinicReferrerId
    ? (() => {
        const member = clinicTeam.find((item) => item.id === clinicReferrerId);
        return { mode: "clinician", clinicianId: member.id, defaultName: `${member.first_name || ""} ${member.last_name || ""}`.trim() + (member.profession ? ` — ${member.profession}` : "") };
      })()
    : { mode: "none", defaultName: "" };

  const signedIn = !!user;

  return (
    <div className="min-h-screen bg-background">
      {!embeddedMode && (sampleMode ? (!viewAsMode && (
        <>
          <AppHeader firstName={demoFirstName || user?.user_metadata?.first_name} role="gp" sampleMode demoLinkMode={demoLinkMode} demoNav={demoNav} demoScope={demoScope} />
          <DemoBanner kind="directory" linkMode={demoLinkMode} exitPath={demoExitPath} onExit={demoOnExit} />
        </>
      )) : (
        <AppHeader firstName={user?.user_metadata?.first_name} role={["clinician", "clinic"].includes(role) ? role : "gp"} />
      ))}
      {!embeddedMode && !sampleMode && <LaunchStatusBanner audience={role || "gp"} />}

      <main className={`mx-auto px-4 py-8 sm:px-6 lg:px-8 animate-fadein ${sampleMode ? "max-w-[1500px]" : "max-w-7xl"}`} data-testid={sampleMode ? "sample-directory-page" : "clinicians-page"}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
              {sampleMode ? "Sample directory" : adminRolloutMode ? "Rollout directory" : "Find a clinician"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {readOnlyMode ? "Review launch-eligible clinician profiles. Referral actions are disabled in Admin rollout view." : "Search the directory, choose a clinician, then complete one of the three referral options shown on their profile."}
            </p>
            {!sampleMode && role === "clinic" && (
              <div className="mt-3 max-w-sm">
                <Label>Referring on behalf of</Label>
                <Select value={clinicReferrerId} onValueChange={setClinicReferrerId}>
                  <SelectTrigger className="mt-1" data-testid="clinic-referrer-select"><SelectValue placeholder="Choose a verified team clinician" /></SelectTrigger>
                  <SelectContent>{clinicTeam.map((member) => <SelectItem key={member.id} value={member.id}>{member.first_name} {member.last_name} — {member.profession}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-500">Only verified clinicians can refer. Do not enter patient information.</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500" data-testid="clinicians-result-count">
              {loading ? "Loading…" : !profession ? "Choose a profession to begin" : `${filtered.length} clinician${filtered.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-md border border-slate-200 bg-white p-4 sm:p-5" data-testid="clinicians-filters">
          {sampleMode && tourStep === 3 && (
            <DemoCoachmark title="Add only the filters that matter" onNext={() => advanceTour(4)} nextLabel="Show me the results" onSkip={skipTourHere} className="mb-4">
              Location, availability, language and gender are optional. The more relevant filters you add, the closer the clinician matches will be—but leaving a filter open will show more options.
            </DemoCoachmark>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              {sampleMode && tourStep === 1 && (
                <DemoCoachmark title="First, choose a profession" onSkip={skipTourHere} className="mb-2">
                  Select Psychologist or Psychiatrist to begin.
                </DemoCoachmark>
              )}
              <Label>Profession</Label>
              <Select value={profession} onValueChange={(value) => { setProfession(value); setSpecialty(""); if (tourStep === 1) advanceTour(2); }}>
                <SelectTrigger data-testid="filter-profession-trigger">
                  <SelectValue placeholder="Select profession" />
                </SelectTrigger>
                <SelectContent>
                  {(sampleMode || adminRolloutMode) && <SelectItem value="all" data-testid="profession-option-all">All professions</SelectItem>}
                  <SelectItem value="Psychologist" data-testid="profession-option-psychologist">Psychologist</SelectItem>
                  <SelectItem value="Psychiatrist" data-testid="profession-option-psychiatrist">Psychiatrist</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              {sampleMode && tourStep === 2 && (
                <DemoCoachmark title="Now narrow by specialty" onSkip={skipTourHere} className="mb-2">
                  Choose one or more specialties that are relevant to the referral.
                </DemoCoachmark>
              )}
              <Label>Clinical expertise (optional)</Label>
              <DirectoryMultiSelect
                value={specialty}
                onChange={(value) => { setSpecialty(value); if (value && tourStep === 2) advanceTour(3); }}
                options={specialtyOptions}
                testId="filter-specialty"
                emptyLabel="Any expertise"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="suburb">Suburb / postcode (optional)</Label>
              <SuburbAutocomplete id="suburb" value={suburb} onChange={handleSuburbChange} onSelect={handleSuburbSelect} includePostcodeInValue placeholder="e.g. Manly or 2095" testId="filter-suburb-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="radius-slider">Radius (optional): {radiusKm} km</Label>
              <input
                id="radius-slider"
                type="range"
                min="1"
                max="50"
                step="1"
                value={radiusKm}
                onChange={(event) => setRadiusKm(event.target.value)}
                className="mt-3 w-full accent-primary"
                data-testid="filter-radius-slider"
              />
              {!suburbLocation && (
                <p className="mt-1 text-xs text-slate-400">Select a suburb or postcode to apply radius</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Availability (optional)</Label>
              <Select value={availability} onValueChange={setAvailability}>
                <SelectTrigger data-testid="filter-availability-trigger">
                  <SelectValue placeholder="Any availability" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="availability-option-all">Any availability</SelectItem>
                  {availabilityOptions.map((v) => (
                    <SelectItem key={v} value={v} data-testid={`availability-option-${v}`}>
                      {availabilityMeta(v).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Appointment type (optional)</Label>
              <Select value={appointmentType} onValueChange={setAppointmentType}>
                <SelectTrigger data-testid="filter-appointment-type">
                  <SelectValue placeholder="Any appointment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any appointment type</SelectItem>
                  {APPOINTMENT_TYPE_OPTIONS.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <>
              <div className="space-y-1.5">
                <Label>Language (optional)</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="filter-language-trigger"><SelectValue placeholder="Any language" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any language</SelectItem>
                    {languageOptions.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Gender (optional)</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger data-testid="filter-gender-trigger"><SelectValue placeholder="Any gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any gender</SelectItem>
                    {genderOptions.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
            <div className="flex items-end lg:col-span-2">
              <div className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-700" data-testid="verified-note">
                <BadgeCheck className="h-4 w-4" /> AHPRA-verified only
              </div>
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3 flex items-center gap-2">
              <SlidersHorizontal className="h-3.5 w-3.5 text-slate-400" />
              <button onClick={clearAll} className="text-sm font-medium text-primary hover:underline" data-testid="clear-filters-button">
                Clear filters
              </button>
            </div>
          )}
            {gpUserId && (
              <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
                <Bell className="h-3.5 w-3.5 text-slate-400" />
                <button
                  type="button"
                  onClick={() => setSaveOpen(true)}
                  className="text-sm font-medium text-primary hover:underline"
                  data-testid="save-search-button"
                >
                  Save this search
                </button>
              </div>
            )}
        </div>

          <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save this search</DialogTitle>
                <DialogDescription>Give it a name and optionally get notified when a new clinician matches these filters.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  placeholder="e.g. Psychologists in Brunswick"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  data-testid="save-search-name-input"
                />
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={saveAlerts} onChange={(e) => setSaveAlerts(e.target.checked)} />
                  Email me when a new clinician matches this search
                </label>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setSaveOpen(false)} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                <button
                  onClick={saveSearch}
                  disabled={!saveName.trim() || saving}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  data-testid="save-search-confirm"
                >
                  {saving ? "Saving\u2026" : "Save search"}
                </button>
              </div>
            </DialogContent>
          </Dialog>

        {/* Results */}
        {sampleMode && tourStep === 4 && !loading && filtered.length > 0 && (
          <DemoCoachmark title="Choose a clinician to view their profile" onSkip={skipTourHere} className="mt-6">
            Open any fictional clinician card to review their specialty, availability and contact details.
          </DemoCoachmark>
        )}
        {loading ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-44 animate-pulse rounded-md border border-slate-200 bg-white" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="clinicians-empty">
            <Search className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">{!profession ? "Choose Psychologist or Psychiatrist to begin" : clinicians.length === 0 ? "The live clinician directory is being prepared" : "No clinicians match your search"}</p>
            <p className="mx-auto mt-1 max-w-xl text-sm text-slate-500">
              {!profession
                ? "All other filters are optional and can be used to narrow your results."
                : clinicians.length === 0
                ? "SimplyRefer is launching in stages. Live clinician availability will appear here when the network is activated; you can explore the Sample Directory in the meantime."
                : "Try adjusting or clearing your filters."}
            </p>
          </div>
        ) : (
          <div className={`mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 ${sampleMode ? "2xl:grid-cols-4" : ""}`} data-testid="clinicians-grid">
            {filtered.map((c) => (
              <ClinicianCard key={c.id} c={c} onView={() => openClinician(c)} />
            ))}
          </div>
        )}
      {sampleMode && !viewAsMode && <DemoFooter linkMode={demoLinkMode} exitPath={demoExitPath} onExit={demoOnExit} />}
      </main>

      {!sampleMode && launchLive && (
        <Dialog open={demoCompletionOpen} onOpenChange={(open) => { setDemoCompletionOpen(open); if (!open) sessionStorage.setItem("sample-directory-completion-dismissed", "1"); }}>
          <DialogContent className="max-w-md bg-white" data-testid="sample-directory-completion-dialog">
            <DialogHeader><DialogTitle>Comfortable with SimplyRefer?</DialogTitle><DialogDescription>If you no longer need the guided Sample Directory, confirm below and the live directory will stay as your default.</DialogDescription></DialogHeader>
            <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">You can reopen the Sample Directory at any time from the top-right of the live directory, directly above the search filters.</div>
            <div className="mt-4 flex flex-wrap justify-end gap-2"><Button variant="outline" onClick={() => { setDemoCompletionOpen(false); navigate("/sample-directory"); }}>Explore Sample Directory</Button><Button onClick={completeSampleDirectory}>I'm comfortable — use live directory</Button></div>
          </DialogContent>
        </Dialog>
      )}

      {!sampleMode && role === "gp" && (
        <Dialog open={introPromptOpen} onOpenChange={() => {}}>
          <DialogContent className="max-w-lg bg-white" data-testid="gp-introduction-opt-in">
            <DialogHeader>
              <DialogTitle>Know the clinicians before you need to refer</DialogTitle>
              <DialogDescription>Would you like SimplyRefer to occasionally introduce relevant psychologists and psychiatrists who are accepting referrals?</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-600">
              <p>Introductions can help you understand a clinician’s expertise, referral preferences and current availability before a patient needs support.</p>
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sky-900">
                SimplyRefer controls delivery. Clinicians never receive your direct email address, cannot bulk-contact your clinic and cannot send you messages unless you opt in.
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={() => saveIntroductionPreference(false)}>No thanks</Button>
                <Button type="button" onClick={() => saveIntroductionPreference(true)}>Yes, receive selected introductions</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ClinicianDialog clinician={selected} referrer={referrer} sampleMode={sampleMode} readOnlyMode={readOnlyMode} demoScope={demoScope} demoTrackEvent={demoTrackEvent} tourStep={tourStep} setTourStep={(stepValue) => advanceTour(stepValue)} onSkipTour={skipTourHere} onClose={() => setSelected(null)} />
    </div>
  );
}

const ClinicianCard = ({ c, onView }) => {
  const av = availabilityMeta(c.availability_status);
  return (
    <button
      onClick={onView}
      data-testid={`clinician-card-${c.id}`}
      className="group flex flex-col rounded-md border border-slate-200 bg-white p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-500"
    >
      <div className="flex items-start gap-3">
        <Avatar c={c} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-heading text-base font-semibold text-slate-900" data-testid={`clinician-name-${c.id}`}>
              {c.first_name} {c.last_name}
            </h3>
            {c.is_verified && (
              <BadgeCheck className="h-4 w-4 shrink-0 text-primary" data-testid={`clinician-verified-${c.id}`} title="Verified" />
            )}
          </div>
          <p className="truncate text-sm text-slate-600">{c.profession}</p>
        </div>
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Expertise</p>
        <p className="line-clamp-1 text-sm font-medium text-slate-800">{expertiseFor(c).join(", ")}</p>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-sm text-slate-500">
        <MapPin className="h-3.5 w-3.5" />
        <span className="truncate">{c.suburb}{c.postcode ? `, ${c.postcode}` : ""}</span>
      </div>

      {c.languages && (
        <div className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-500">
          <Languages className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate" data-testid={`clinician-languages-${c.id}`}>{c.languages}</span>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium ${av.tone}`} style={av.style}>
          <CalendarClock className="h-3 w-3" /> {av.label}
        </span>
              {(c.assessment_291_status === "AVAILABLE_NOW" || c.assessment_291_status === "AVAILABLE_FROM") && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium" style={{ backgroundColor: "#FFFBEB", color: "#92400E", borderColor: "#FCD34D" }}>
                  291 assessment{c.assessment_291_status === "AVAILABLE_FROM" && c.assessment_291_available_from ? ` from ${c.assessment_291_available_from}` : ""}
                </span>
              )}
        <span className="text-sm font-medium text-primary opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          View →
        </span>
      </div>
      {capacityCardText(c) && (
        <span className="mt-2 inline-flex w-fit items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700" data-testid={`clinician-capacity-${c.id}`}>
          <Users className="h-3 w-3" /> {capacityCardText(c)}
        </span>
      )}
    </button>
  );
};

const ClinicianDialog = ({ clinician, referrer, sampleMode = false, readOnlyMode = false, demoScope = "sample-demo", demoTrackEvent, tourStep = 99, setTourStep, onSkipTour, onClose }) => {
  const c = clinician;
  const av = c ? availabilityMeta(c.availability_status) : null;
  const [step, setStep] = useState("idle"); // idle | form | done | selected
  const [generating, setGenerating] = useState(false);
  const [clinicName, setClinicName] = useState("");
  const [token, setToken] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [tokenIntent, setTokenIntent] = useState("token");
  const [capDates, setCapDates] = useState([]);
  const isClinician = referrer?.mode === "clinician";

  // Load staggered opening dates for the open clinician (defensive: table may not exist yet).
  useEffect(() => {
    if (!c?.id || c.capacity_timing_type !== "staggered") { setCapDates([]); return; }
    let active = true;
    (async () => {
      try {
        const { data } = await supabase
          .from("clinician_capacity_dates")
          .select("patient_count,available_from")
          .eq("clinician_id", c.id);
        if (active) setCapDates(data || []);
      } catch { if (active) setCapDates([]); }
    })();
    return () => { active = false; };
  }, [c?.id, c?.capacity_timing_type]);

  // Reset the token UI whenever a different clinician is opened
  useEffect(() => {
    setStep("idle");
    setGenerating(false);
    setClinicName(referrer?.defaultName || "");
    setToken("");
    setSelectedOption("");
    setTokenIntent("token");
  }, [c?.id, referrer?.defaultName]);

  const snippet = c && token ? bookingSnippet({ token, clinician: c }) : "";

  const generate = async () => {
    if (!referrer || referrer.mode === "none") {
      toast.error("Your profile isn't ready yet. Please try again in a moment.");
      return;
    }
    if (!clinicName.trim()) {
      toast.error(isClinician ? "Please enter your name or practice." : "Please enter your GP clinic name.");
      return;
    }
    // Sample mode: browser-only demo token. Nothing is saved to Supabase or sent.
    if (sampleMode) {
      const record = saveDemoToken(demoScope, c, clinicName.trim());
      setToken(record.token);
      setStep("done");
      if (setTourStep && tourStep < 99) setTourStep(7);
      if (demoTrackEvent) demoTrackEvent("TOKEN_GENERATED", { clinician_id: c.id, profession: c.profession, specialties: c.specialty });
      toast.success(`Sample SimplyRefer Reference #${record.token} created (nothing saved or sent).`);
      if (tokenIntent === "email") window.setTimeout(() => emailClinician(record.token), 0);
      return;
    }
    setGenerating(true);
    const newToken = genToken();
    const row = referrer.mode === "gp"
      ? { token: newToken, clinician_id: c.id, gp_user_id: referrer.gpUserId, gp_clinic_name: clinicName.trim() }
      : { token: newToken, clinician_id: c.id, referrer_clinician_id: referrer.clinicianId, gp_clinic_name: clinicName.trim() };
    const { error } = await supabase.from("booking_tokens").insert(row);
    setGenerating(false);
    if (error) {
      toast.error("Could not create the SimplyRefer Reference: " + error.message);
      return;
    }
    setToken(newToken);
    setStep("done");
    toast.success(`SimplyRefer Reference #${newToken} created.`);
    authedFetch("/notify/booking-token", {
      method: "POST",
      body: JSON.stringify({
        clinician_id: c.id,
        gp_clinic_name: clinicName.trim(),
        token: newToken,
        referrer_clinician_id: referrer.mode === "clinician" ? referrer.clinicianId : null,
      }),
    }).catch(() => {});
    if (tokenIntent === "email") window.setTimeout(() => emailClinician(newToken), 0);
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success("SimplyRefer Reference copied.");
    } catch {
      toast.error("Copy failed — select and copy the SimplyRefer Reference manually.");
    }
  };

  const emailClinician = (reference = token) => {
    const to = c.email ? encodeURIComponent(c.email) : "";
    const emailSnippet = bookingSnippet({ token: reference, clinician: c });
    const subject = encodeURIComponent((sampleMode ? "[DEMO] " : "") + `Referral to ${c.first_name} ${c.last_name} — SimplyRefer Reference #${reference}`);
    const body = sampleMode
      ? emailSnippet + "\n\nDEMO ONLY — This email is addressed to a fictional clinician and should not be sent."
      : emailSnippet;
    const mailBody = encodeURIComponent(body);
    if (sampleMode) {
      if (setTourStep && tourStep < 99) setTourStep(99);
      if (demoTrackEvent) demoTrackEvent("EMAIL_DRAFT_OPENED", { clinician_id: c.id });
    }
    window.location.href = `mailto:${to}?subject=${subject}&body=${mailBody}`;
  };

  const selectForReferral = async () => {
    if (step === "selected") return;
    setSelectedOption("select");
    setStep("selected");
    if (sampleMode) {
      demoTrackEvent?.("SELECTED_FOR_REFERRAL", { clinician_id: c.id, profession: c.profession });
    } else if (referrer?.mode === "gp") {
      authedFetch("/analytics/clinician-events", {
        method: "POST",
        body: JSON.stringify({ event_type: "SELECTED_FOR_REFERRAL", clinician_ids: [c.id] }),
      }).catch(() => {});
    }
    toast.success("Clinician selected. Continue using your usual referral process or system.");
  };

  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-white" data-testid="clinician-profile-dialog">
        {c && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-4">
                <Avatar c={c} size="lg" />
                <div>
                  <DialogTitle className="flex items-center gap-2 font-heading text-xl">
                    {c.first_name} {c.last_name}
                    {c.is_verified && <BadgeCheck className="h-5 w-5 text-primary" />}
                  </DialogTitle>
                  <p className="text-sm text-slate-600">{c.profession}</p>
                </div>
              </div>
              <DialogDescription className="sr-only">Clinician profile and three SimplyRefer referral options.</DialogDescription>
            </DialogHeader>

            {step !== "done" && (
              <div className="mt-2 space-y-4">
              <Row label="Clinical expertise" value={expertiseFor(c).join(", ")} icon={Stethoscope} testId="profile-specialty" />
              {(c.appointment_types || []).length > 0 && <Row label="Appointments" value={c.appointment_types.join(", ")} icon={CalendarClock} testId="profile-appointment-types" />}
              {c.practice_address && <Row label="Practising address" value={c.practice_address} icon={MapPin} testId="profile-practice-address" />}
              <Row label="Location" value={`${c.suburb}${c.state ? ", " + c.state : ""}${c.postcode ? " " + c.postcode : ""}`} icon={MapPin} testId="profile-location" />
              {c.languages && <Row label="Languages" value={c.languages} icon={Languages} testId="profile-languages" />}
              <div className="flex items-start gap-3">
                <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Availability</p>
                  <span className={`mt-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium ${av.tone}`} style={av.style} data-testid="profile-availability">
                    {av.label}{c.wait_time ? ` · ${c.wait_time}` : ""}
                  </span>
              {(c.assessment_291_status === "AVAILABLE_NOW" || c.assessment_291_status === "AVAILABLE_FROM") && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium" style={{ backgroundColor: "#FFFBEB", color: "#92400E", borderColor: "#FCD34D" }}>
                  291 assessment{c.assessment_291_status === "AVAILABLE_FROM" && c.assessment_291_available_from ? ` from ${c.assessment_291_available_from}` : ""}
                </span>
              )}
                  {c.availability_last_confirmed_at && (() => { const days = Math.floor((Date.now() - new Date(c.availability_last_confirmed_at).getTime()) / 86400000); const label = days <= 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`; return <p className="mt-1 text-xs text-slate-500">Confirmed {label}</p>; })()}
                  {(() => { const detail = capacityDetail(c, capDates); return detail ? (
                    <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-2.5" data-testid="profile-capacity">
                      <p className="flex items-center gap-1 text-xs font-semibold text-blue-800"><Users className="h-3.5 w-3.5" /> Current openings</p>
                      <ul className="mt-1 space-y-0.5 text-sm text-blue-900">
                        {detail.map((line, i) => <li key={i}>{line}</li>)}
                      </ul>
                    </div>
                  ) : null; })()}
                  {psychReferralLabel(c.psychiatry_referral_type) && (
                    <p className="mt-2 text-xs text-slate-600" data-testid="profile-psych-referral">
                      Accepting: <span className="font-medium text-slate-800">{psychReferralLabel(c.psychiatry_referral_type)}</span>
                    </p>
                  )}
                </div>
              </div>
              {c.email && <Row label="Email" value={c.email} icon={Mail} testId="profile-email" />}
              {c.phone && <Row label="Phone" value={c.phone} icon={Phone} testId="profile-phone" />}
              </div>
            )}

            {/* Referral options */}
            <div className={step === "done" ? "mt-2" : "mt-5 border-t border-slate-200 pt-5"}>
              {readOnlyMode ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="admin-rollout-read-only">
                  <p className="font-semibold">Admin rollout preview</p>
                  <p className="mt-1">This is a real launch-eligible profile. Referral actions are disabled, and no customer record is changed.</p>
                </div>
              ) : step === "done" ? (
                <div className="space-y-3" data-testid="booking-token-success">
                  {sampleMode && tourStep === 7 && (
                    <DemoCoachmark title="Use your usual referral process" onSkip={onSkipTour}>
                      Your SimplyRefer Reference is ready. Copy it into your usual referral or use the prefilled email option you selected.
                    </DemoCoachmark>
                  )}
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-accent" />
                      <p className="font-medium text-slate-900">SimplyRefer Reference <span className="font-mono" data-testid="booking-token-value">#{token}</span></p>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Ready to include in your referral to {c.first_name} {c.last_name}.</p>
                    {sampleMode && <p className="mt-1 text-xs font-medium text-amber-700">This is a sample reference. Nothing has been saved or sent.</p>}
                  </div>

                  {tokenIntent === "email" && (
                    <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
                      <p className="text-sm text-slate-700">Your email window should open with the clinician's address, subject, message and SimplyRefer Reference already filled in. Attach your usual referral letter, review and send.</p>
                      <Button className="mt-3 h-auto w-full py-3 text-base" onClick={() => emailClinician()} data-testid="booking-token-email">
                        <Mail className="mr-2 h-4 w-4" /> Open prefilled email again
                      </Button>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Button className="w-full sm:w-auto" onClick={copyToken} data-testid="booking-token-copy">
                      <Copy className="mr-2 h-4 w-4" /> Copy SimplyRefer Reference
                    </Button>
                    <Button variant="ghost" onClick={() => { setStep("idle"); setToken(""); setSelectedOption(""); }} data-testid="booking-token-new">Choose another referral option</Button>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Prefilled email preview</p>
                    <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700" data-testid="booking-token-snippet">{snippet}</pre>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {sampleMode && tourStep === 5 && (
                    <DemoCoachmark title="Create the referral reference" onSkip={onSkipTour}>
                      Choose one of the three referral options. Each option lets the clinician know that their availability information helped a GP choose them.
                    </DemoCoachmark>
                  )}
                  <p className="text-sm leading-relaxed text-slate-600">
                    Choose how you would like to complete this referral. Select one option below. Each option lets the clinician know that their SimplyRefer availability helped you choose them and encourages them to keep availability and wait times up to date.
                  </p>
                  <div className="grid gap-3">
                    <ReferralOption
                      selected={selectedOption === "select"}
                      primary
                      icon={MousePointerClick}
                      title="Select for Referral"
                      description="Records the referral in SimplyRefer so the clinician can see that you referred to them using your usual system. No patient information is shared through SimplyRefer."
                      onClick={selectForReferral}
                      testId="select-for-referral-button"
                    />
                    <ReferralOption
                      selected={selectedOption === "reference"}
                      icon={KeyRound}
                      title="Create SimplyRefer Reference"
                      description="Creates a SimplyRefer Reference number to copy into the referral you send through your usual system. The clinician can use it to confirm the referral came through SimplyRefer."
                      onClick={() => { setSelectedOption("reference"); setTokenIntent("token"); setStep("form"); if (sampleMode && setTourStep && tourStep === 5) setTourStep(6); }}
                      testId="secure-booking-token-button"
                    />
                    <ReferralOption
                      selected={selectedOption === "email"}
                      icon={Mail}
                      title="Generate Email"
                      description="Opens a prefilled email with the clinician's address, subject, message and a SimplyRefer Reference. Attach your referral letter, review and send."
                      onClick={() => { setSelectedOption("email"); setTokenIntent("email"); setStep("form"); if (sampleMode && setTourStep && tourStep === 5) setTourStep(6); }}
                      testId="generate-email-button"
                    />
                  </div>
                  {step === "selected" && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-900" data-testid="referral-selected-message">
                      Selected. Continue using your usual referral process or system. The clinician can see this selection in their SimplyRefer Insights.
                    </div>
                  )}
                  {step === "form" && (
                    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="booking-token-form">
                      {sampleMode && tourStep === 6 && (
                        <DemoCoachmark title={tokenIntent === "email" ? "Create the prefilled email" : "Create a fictional SimplyRefer Reference"} onSkip={onSkipTour}>
                          The demo clinic name is already filled in. No patient information is requested or stored.
                        </DemoCoachmark>
                      )}
                      <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                        <p className="text-xs text-sky-800">{sampleMode ? "This is a sample. Nothing you enter or create here is saved or sent." : `No patient information is collected or stored. We only record your ${isClinician ? "name / practice" : "clinic name"} and the SimplyRefer Reference.`}</p>
                      </div>
                      <Label htmlFor="gp-clinic-name">{isClinician ? "Your name & practice" : "Your GP clinic name"}</Label>
                      <Input
                        id="gp-clinic-name"
                        value={clinicName}
                        onChange={(e) => setClinicName(e.target.value)}
                        placeholder={isClinician ? "e.g. Dr Jane Smith — Psychology" : "e.g. Bayside Family Practice"}
                        data-testid="booking-token-clinic-input"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" onClick={() => { setStep("idle"); setSelectedOption(""); }} disabled={generating} data-testid="booking-token-cancel">Cancel</Button>
                        <Button onClick={generate} disabled={generating} data-testid="booking-token-generate">
                          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : tokenIntent === "email" ? <Mail className="mr-2 h-4 w-4" /> : <KeyRound className="mr-2 h-4 w-4" />}
                          {tokenIntent === "email" ? "Generate Email" : "Create SimplyRefer Reference"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={onClose} data-testid="profile-close-button">
                <X className="mr-2 h-4 w-4" /> Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ReferralOption = ({ selected, primary = false, icon: Icon, title, description, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={"flex w-full items-start gap-3 rounded-md border p-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 " + (primary ? "border-primary bg-primary text-white hover:bg-primary/95 " : "border-slate-200 bg-white text-slate-900 hover:border-sky-300 hover:bg-sky-50 ") + (selected ? "ring-2 ring-sky-300" : "")}
    data-testid={testId}
  >
    <Icon className="mt-0.5 h-5 w-5 shrink-0" />
    <span className="min-w-0 flex-1">
      <span className="block font-semibold">{title}</span>
      <span className={`mt-1 block text-sm leading-relaxed ${primary ? "text-blue-100" : "text-slate-600"}`}>{description}</span>
    </span>
    <span className={"mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 " + (selected ? "border-sky-500 bg-white text-primary" : primary ? "border-blue-200" : "border-slate-300")}>
      {selected && <CheckCircle2 className="h-4 w-4" />}
    </span>
  </button>
);

const Row = ({ label, value, icon: Icon, testId }) => (
  <div className="flex items-start gap-3">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800" data-testid={testId}>{value}</p>
    </div>
  </div>
);
